/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import mbpLogger from '@module-logger/mbp';
import { useSelector } from 'react-redux';
import {
    shape, string, array,
    bool,
} from 'prop-types';
// eslint-disable-next-line import/no-cycle
import GenericSkeleton from '../../GraphqlSkeletonComponents/GenericSkeleton';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
// eslint-disable-next-line import/no-cycle
import SiteWideBannerContainer from '../../GraphqlHeader/SiteWideBanner/SiteWideBannerContainer';
import FallbackContentContainer from '../Common/FallbackContentContainer';
import cookies from '../../../../helpers/common/storage/cookies';
import { getIsBot } from '../../../../../state/ducks/App/App-Selectors';
import useOfferGatewayExperience from '../../../../helpers/Personalization/useOfferGatewayExperience';

const PersonalizedSiteWideBannerContainer = ({
    pz_campaign_id,
    content_zones,
    fallbackUID,
    contentType,
    skeleton_height,
    skeleton_width,
    skeleton_margin = '0 auto',
    isOfferGatewayEnabled,
}) => {
    const isBot = useSelector(getIsBot);
    const persistedUserProfile = cookies.get('persisted_user_profile');

    if (isBot) {
        return (
            <FallbackContentContainer
                skeleton_height={skeleton_height}
                skeleton_width={skeleton_width}
                fallbackUID={fallbackUID}
                contentType={contentType}
            />
        );
    }

    if (typeof window === 'undefined') {
        return <GenericSkeleton height={skeleton_height} width={skeleton_width} maxWidth="unset" margin={skeleton_margin} id="PersonalizedSiteWideBannerContainer" />;
    }

    const interactionName = pz_campaign_id;

    const targeting = {
        pageType: 'home',
    };

    if (content_zones?.length && content_zones?.[0]) {
        targeting.contentZones = content_zones;
    }

    const {
        loading,
        error,
        data,
        variables,
    } = useOfferGatewayExperience({
        interactionName,
        targeting,
        type: 'content',
        resolveIdentity: true,
        queryName: 'Personalized_site_wide_banner',
        isSessionHasAccount: !!persistedUserProfile,
    });

    const content = data?.experience?.content;
    const experimentTracking = data?.experience?.experimentTracking;

    const trackClickThrough = useClickStreamCustomEventsTracking({
        experimentTracking,
        isFireImpression: true,
        page: { type: 'home' },
    });

    if (loading || variables?.skip) {
        return (
            <GenericSkeleton
                height={skeleton_height}
                width={skeleton_width}
                maxWidth="unset"
                margin={skeleton_margin}
            />
        );
    }

    if (error) {
        mbpLogger.logError({
            function: `PersonalizedSiteWideBannerContainer - interactionName = ${interactionName}`,
            message: 'Error loading API data',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
        return (
            <FallbackContentContainer
                skeleton_height={skeleton_height}
                skeleton_width={skeleton_width}
                fallbackUID={fallbackUID}
                contentType={contentType}
            />
        );
    }

    if (content) {
        return (
            <div data-testid={content?.message}>
                <SiteWideBannerContainer
                    bannerData={content}
                    trackClickThrough={trackClickThrough}
                    fireClickThrough={!isOfferGatewayEnabled}
                    isOfferGatewayEnabled={isOfferGatewayEnabled}
                // salesforceResponse={sfData} // avoid offergateway but offerinbox still works
                />
            </div>
        );
    }
    return (
        <FallbackContentContainer
            skeleton_height={skeleton_height}
            skeleton_width={skeleton_width}
            fallbackUID={fallbackUID}
            contentType={contentType}
            trackClickThrough={trackClickThrough}
        // salesforceResponse={sfData} // avoid salesforce functionality
        />
    );
};

PersonalizedSiteWideBannerContainer.propTypes = {
    pz_campaign_id: string,
    content_zones: array,
    fallbackUID: string,
    contentType: string,
    skeleton_height: string,
    skeleton_width: string,
    skeleton_margin: string,
    isOfferGatewayEnabled: bool,
};

PersonalizedSiteWideBannerContainer.defaultProps = {
    pz_campaign_id: null,
    content_zones: [],
    fallbackUID: null,
    contentType: null,
    skeleton_height: null,
    skeleton_width: null,
    skeleton_margin: null,
    isOfferGatewayEnabled: false,
};

export default PersonalizedSiteWideBannerContainer;
